import {
    BarIcon,
    CalendarIcon,
    CardIcon,
    CategoryIcon,
    ExternalDataImage,
    FileImageIcon,
    GaugeIcon,
    ImageIcon,
    LineIcon,
    LineSmoothIcon,
    MapIcon,
    PieIcon,
    SubscriptionIcon,
    TableIcon,
    RoomIcon,
    LDSUWidgetIcon,
} from "assets/svg/widget";
import { APP_CONFIG } from "config/appConfig";

import {
    LDSUOverviewImage,
    LDSUReadingImage,
    // Sensor Widget
    AggregateCountImage,
    SingleSensorImage,
    MultiSensorImage,
    HeatmapCountSensorImage,
    AnySensorImage,
    TemperatureSensorImage,
    IAQSensorImage,
    HumiditySensorImage,
    AmbientLightSensorImage,
    MotionSensorImage,
    MultiValuesSensorImage,
    SensorReadingImage,
    SensorReadingGradeImage,

    // Actuator Image
    ActuatorTriggerImage,
    MultiSameActuatorImage,
    ActuatorCorrelationImage,

    // Gateway Image
    GatewayOverviewImage,
    GatewayListWithStatusImage,
    GatewayUpTimeHistoryImage,
    GatewayStatusImage,
    GatewayStorageImage,

    // Meter Image
    EnergyConsumptionImage,
    WaterConsumptionImage,

    // PSL Image
    RGZStatusImage,
    SingleRoomUsageImage,
    MultiRoomUsageImage,
    RoomStatusTimelineImage,
    ZoneStatusTimelineImage,
    GroupStatusTimelineImage,

    // Subscription Image
    TokenDonutImage,
    TokenStackedBar,

    // Event Image
    EventCountImage,
    NotifierCountImage,
} from "assets/img/widget";
import {
    WidgetTypeList,
    WidgetSourceKey,
    WidgetItem,
    WidgetTypeKey,
    WidgetCategory,
    WidgetSource,
} from "types";
import { WidgetType } from "components/dashboard/utils/widgetConfig";

const ldsuDisplayName = APP_CONFIG.APP_IS_ONPREM ? "LDSU/Auxiliary Hub" : "LDSU";

// --- Data ---
export const widgetTemplates: Record<WidgetSourceKey, Array<WidgetItem>> = {
    sensor: [
        {
            id: 1,
            name: "Chart - Real-time sensor data",
            description: "View live data from a single sensor in real time.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SENSOR_CHART_SINGLE,
        },
        {
            id: 2,
            name: "Chart - Multi-sensor comparison",
            description:
                "Compare data across multiple sensors in a single view.",
            ImagePreview: MultiSensorImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SENSOR_CHART_MULTIPLE,
        },
        {
            id: 3,
            name: "Chart - Aggregate count from sensors",
            description: "Monitor the total count from selected sensors.",
            ImagePreview: AggregateCountImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SENSOR_CHART_AGGREGATE,
        },
        {
            id: 4,
            name: "Chart - Heatmap for people count sensors",
            description: "Visualizes density using data from multiple PCS.",
            ImagePreview: HeatmapCountSensorImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SENSOR_PCS_COMBINED_HEATMAP,
        },
        {
            id: 5,
            name: "Card - Any sensor",
            description: "Card to monitor readings from any type of sensor.",
            ImagePreview: AnySensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
        {
            id: 6,
            name: "Card - Temperature sensor",
            description:
                "Card to monitor readings from the temperature sensor.",
            ImagePreview: TemperatureSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_TEMPERATURE,
        },
        {
            id: 7,
            name: "Card - IAQ sensor",
            description: "Card to monitor readings from the IAQ sensor.",
            ImagePreview: IAQSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_IAQ,
        },
        {
            id: 8,
            name: "Card - Humidity sensor",
            description: "Card to monitor readings from the humidity sensor.",
            ImagePreview: HumiditySensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_HUMIDITY,
        },
        {
            id: 9,
            name: "Card - Ambient light sensor",
            description:
                "Card to monitor readings from the ambient light sensor.",
            ImagePreview: AmbientLightSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_AMBIENT,
        },
        {
            id: 10,
            name: "Card - Motion sensor",
            description: "Card to monitor alerts from the motion sensor.",
            ImagePreview: MotionSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_MOTION,
        },
        {
            id: 11,
            name: "Card - Multi values",
            description: "Monitor readings from multiple sensors.",
            ImagePreview: MultiValuesSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_MULTI_VALUES,
        },
        {
            id: 12,
            name: "Dial - Sensor reading",
            description: "Gauge to monitor readings from any sensor.",
            ImagePreview: SensorReadingImage,
            type: WidgetTypeList.Dial,
            widgetType: WidgetType.SENSOR_DIAL_READING,
        },
        {
            id: 13,
            name: "Dial - Sensor reading with grades",
            description: "Gauge to monitor readings from any sensor.",
            ImagePreview: SensorReadingGradeImage,
            type: WidgetTypeList.Dial,
            widgetType: WidgetType.SENSOR_DIAL_READING_GRADE,
        },
    ],
    actuator: [
        {
            id: 1,
            name: "Chart - Actuator trigger data",
            description: "Visualizes when actuator was activated/deactivated.",
            ImagePreview: ActuatorTriggerImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.ACTUATOR_CHART_SINGLE,
        },
        {
            id: 2,
            name: "Chart - Multi-actuator comparison",
            description:
                "Compares multiple actuator activities in a single view.",
            ImagePreview: MultiSameActuatorImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.ACTUATOR_CHART_MULTIPLE,
        },
        {
            id: 3,
            name: "Chart - Actuator correlation with sensor",
            description: "Compares actuator activity with sensor inputs.",
            ImagePreview: ActuatorCorrelationImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.ACTUATOR_CHART_CORRELATION,
        },
    ],
    gateway: [
        {
            id: 1,
            name: "Card - Gateway Overview",
            description: "View gateway's details and no. of connected devices.",
            ImagePreview: GatewayOverviewImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.GATEWAY_CARD_OVERVIEW,
        },
        {
            id: 2,
            name: "Table - List of Gateways with Status",
            description: "View status of the selected gateways at a glance.",
            ImagePreview: GatewayListWithStatusImage,
            type: WidgetTypeList.Table,
            widgetType: WidgetType.GATEWAY_TABLE_STATUS,
        },
        {
            id: 3,
            name: "Card - Gateway Uptime History",
            description: "Uptime history of gateways over the last 90 days.",
            ImagePreview: GatewayUpTimeHistoryImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.GATEWAY_CARD_UPTIME_HISTORY,
        },
        {
            id: 4,
            name: "Chart - Gateway Status Statistics",
            description:
                "Visualize status of a gateway over different time ranges.",
            ImagePreview: GatewayStatusImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.GATEWAY_CHART_STATUS_STATISTICS,
        },
        {
            id: 5,
            name: "Chart - Gateway Storage Usage",
            description: "View how much storage is used by a gateway.",
            ImagePreview: GatewayStorageImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.GATEWAY_CHART_STORAGE_USAGE,
        },
    ],
    psl: [
        {
            id: 1,
            name: "Table - Room/Zone/Group Status",
            description: "View live status of Rooms/Zones/Groups.",
            ImagePreview: RGZStatusImage,
            type: WidgetTypeList.Table,
            widgetType: WidgetType.PSL_TABLE_STATUS,
        },
        {
            id: 2,
            name: "Chart - Single Room Usage",
            description: "Detailed usage data for a selected room.",
            ImagePreview: SingleRoomUsageImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.PSL_CHART_SINGLE_ROOM,
        },
        {
            id: 3,
            name: "Chart - Multiple Room Usage",
            description: "Cumulative usage data from multiple rooms.",
            ImagePreview: MultiRoomUsageImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.PSL_CHART_MULTIPLE_ROOM,
        },
        {
            id: 4,
            name: "Chart - Room Status Timeline",
            description: "Visualizes the status of a room over a period.",
            ImagePreview: RoomStatusTimelineImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.PSL_CHART_ROOM_TIMELINE,
        },
        {
            id: 5,
            name: "Chart - Zone Status Timeline",
            description: "Visualizes the status of a zone over a period",
            ImagePreview: ZoneStatusTimelineImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.PSL_CHART_ZONE_TIMELINE,
        },
        {
            id: 6,
            name: "Chart - Group Status Timeline",
            description: "Visualizes the status of a group over a period",
            ImagePreview: GroupStatusTimelineImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.PSL_CHART_GROUP_TIMELINE,
        },
    ],
    meter: [
        {
            id: 1,
            name: "Energy Consumption",
            description: "Track total consumption from energy meters.",
            ImagePreview: EnergyConsumptionImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.METER_CHART_ENERGY_CONSUMPTION,
        },
        {
            id: 2,
            name: "Water Consumption",
            description: "Track total consumption from water flow meters.",
            ImagePreview: WaterConsumptionImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.METER_CHART_WATER_CONSUMPTION,
        },
    ],
    event: [
        {
            id: 1,
            name: "Chart - Count of Events enabled vs triggered",
            description: "Compare enabled events against those triggered.",
            ImagePreview: EventCountImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.EVENT_CHART_ALERT_TRIGGER_COUNT,
        },
        {
            id: 2,
            name: "Chart - Count of emails, SMS, and notifications",
            description:
                "Track total alerts (emails, SMS, notifications) sent.",
            ImagePreview: NotifierCountImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.EVENT_CHART_NOTIFIER_COUNT,
        },
    ],
    subscription: [
        {
            id: 1,
            name: "Chart - Token Spending Distribution ",
            description: "View the percentage breakdown of token usage.",
            ImagePreview: TokenDonutImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SUBSCRIPTION_CHART_TOKEN_DONUT,
        },
        {
            id: 2,
            name: "Chart - Token Spending",
            description: "Track token consumption across services.",
            ImagePreview: TokenStackedBar,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SUBSCRIPTION_CHART_TOKEN_STACKED,
        },
    ],
    ldsu: [
        {
            id: 1,
            name: `Card - ${ldsuDisplayName} Readings`,
            description: `View all readings from a single ${ldsuDisplayName}.`,
            ImagePreview: LDSUOverviewImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.LDSU_CARD_READINGS,
        },
        {
            id: 2,
            name: `Card - ${ldsuDisplayName} Overview with Readings`,
            description: `View ${ldsuDisplayName}'s details with status and all readings.`,
            ImagePreview: LDSUReadingImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.LDSU_CARD_OVERVIEW_READINGS,
        },
    ],
    externalData: [
        {
            id: 1,
            name: "External Data Widget",
            description: "Widget for external data sources.",
            ImagePreview: ExternalDataImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.EXTERNAL_DATA_CARD_COMBINED,
        },
    ],
};

export const widgetTypes: Record<WidgetTypeKey, Array<WidgetItem>> = {
    card: [
        {
            id: 1,
            name: "Card",
            description:
                "Display key sensor or device values in a compact card.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Card,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
    chart: [
        {
            id: 2,
            name: "Chart",
            description:
                "Visualize data trends over time with line, bar, or area charts.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Chart,
            widgetType: WidgetType.SENSOR_CHART_SINGLE,
        },
    ],
    dial: [
        {
            id: 3,
            name: "Dial",
            description: "Show a value in a gauge or dial format.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Dial,
            widgetType: WidgetType.SENSOR_DIAL_READING,
        },
    ],
    image: [
        {
            id: 4,
            name: "Image",
            description: "Display an image widget.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Image,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
    calendar: [
        {
            id: 5,
            name: "Calendar",
            description: "Show calendar-based data.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Calendar,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
    table: [
        {
            id: 6,
            name: "Table",
            description: "Tabular view for multiple sensor/device readings.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Table,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
    maps: [
        {
            id: 7,
            name: "Map",
            description: "Display data on a map.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.Maps,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
    imageLayout: [
        {
            id: 8,
            name: "Image Layout",
            description: "Custom image layout widget.",
            ImagePreview: SingleSensorImage,
            type: WidgetTypeList.ImageLayout,
            widgetType: WidgetType.SENSOR_CARD_ANY,
        },
    ],
};

// Tab options
export const tabOptions = [
    { key: "templates", label: "Widget Templates" },
    //{ key: "types", label: "Widget Types" },
];

// Widget Template Categories
const allWidgetTemplateCategories: WidgetCategory[] = [
    {
        id: WidgetSource.Sensor,
        label: "Sensor related widgets",
        Icon: LineSmoothIcon,
        widgets: widgetTemplates["sensor"],
    },
    {
        id: WidgetSource.Actuator,
        label: "Actuator related widgets",
        Icon: LineIcon,
        widgets: widgetTemplates["actuator"],
    },
    {
        id: WidgetSource.LDSU,
        label: `${ldsuDisplayName} related widgets`,
        Icon: LDSUWidgetIcon,
        widgets: widgetTemplates["ldsu"],
    },
    {
        id: WidgetSource.Gateway,
        label: "Gateway related widgets",
        Icon: PieIcon,
        widgets: widgetTemplates["gateway"],
    },
    {
        id: WidgetSource.PSL,
        label: "Room/Zone/Group widgets",
        Icon: RoomIcon,
        widgets: widgetTemplates["psl"],
    },
    {
        id: WidgetSource.Meter,
        label: "Meter related widgets",
        Icon: CategoryIcon,
        widgets: widgetTemplates["meter"],
    },
    {
        id: WidgetSource.Event,
        label: "Event and alert related widgets",
        Icon: BarIcon,
        widgets: widgetTemplates["event"],
    },
    {
        id: WidgetSource.Subscription,
        label: "Subscription related widgets",
        Icon: SubscriptionIcon,
        widgets: widgetTemplates["subscription"],
    },
    // {
    //     id: WidgetSource.ExternalData,
    //     label: "Widgets for external data",
    //     Icon: ExternalIcon,
    //     widgets: widgetTemplates["externalData"],
    // },
];

// Exclude deployment-specific categories from widget templates.
const onPremExcludedWidgetSources: WidgetSource[] = [WidgetSource.Subscription, WidgetSource.Event];
const cloudExcludedWidgetSources: WidgetSource[] = [WidgetSource.PSL];

const excludedWidgetSources = APP_CONFIG.APP_IS_ONPREM
    ? onPremExcludedWidgetSources
    : cloudExcludedWidgetSources;

export const widgetTemplateCategories: WidgetCategory[] =
    allWidgetTemplateCategories.filter(
        (category) =>
            !excludedWidgetSources.includes(category.id as WidgetSource)
    );

// Widget Type Categories
export const widgetTypeCategories: WidgetCategory[] = [
    {
        id: WidgetTypeList.Card,
        label: "Cards",
        Icon: CardIcon,
        widgets: widgetTypes["card"],
    },
    {
        id: WidgetTypeList.Chart,
        label: "Charts",
        Icon: BarIcon,
        widgets: widgetTypes["chart"],
    },
    {
        id: WidgetTypeList.Dial,
        label: "Dials",
        Icon: GaugeIcon,
        widgets: widgetTypes["dial"],
    },
    {
        id: WidgetTypeList.Image,
        label: "Image",
        Icon: ImageIcon,
        widgets: widgetTypes["image"],
    },
    {
        id: WidgetTypeList.Calendar,
        label: "Calendar",
        Icon: CalendarIcon,
        widgets: widgetTypes["calendar"],
    },
    {
        id: WidgetTypeList.Table,
        label: "Table",
        Icon: TableIcon,
        widgets: widgetTypes["table"],
    },
    {
        id: WidgetTypeList.Maps,
        label: "Maps",
        Icon: MapIcon,
        widgets: widgetTypes["maps"],
    },
    {
        id: WidgetTypeList.ImageLayout,
        label: "Image Layout",
        Icon: FileImageIcon,
        widgets: widgetTypes["imageLayout"],
    },
];
